/**
 * Phase 6e2c-2 — EVAP PACK selector (§33.9).
 *
 * React port of components/evap_pack/view/evap_pack.selector.js (249) + its
 * tmpl, ported into the 6e2c-0+1 component-selector chassis (§30) as a tab body.
 * BOARD only; opened from the evap_pack FORM's rating-mode label
 * (useComponentForm.openRatingStub → rating_mode:activate {name:'evap_pack',
 * current_model:{id:row.id}, section}). The chassis fan-out key is therefore
 * evap_pack_<componentId>:selector:<action>.
 *
 * STAGING MODEL (legacy initialize): a working copy of the evap_pack component
 * model, seeded from the read-only
 * GET ahu/evap_packs/<id> (reuse getComponentDetail). 5 params JSON blobs
 * (media/pump/fill/drain/overflow_params) each with its `.X_params_field`
 * handler; automatic-valve lists derived from *_automatic_valve_ids arrays ×
 * CMS (legacy _buildValveLists). Tabs media/construction/pump/options restored
 * across re-renders (the legacy tmpl's four rail items; the fill/drain/overflow
 * params families live as subheadings under the Pump & Piping tab).
 *
 * DOM SHAPE IS LOAD-BEARING. The legacy markup is three nested things the port
 * originally collapsed into one: root `#evap_pack_new.input-part.row.evap_pack
 * .evap_pack-rating-mode`, a `col-xs-1.nav.nav-tabs` SIDEBAR rail, and a
 * `.output-part.evap_pack-output-section` SIBLING carrying its own rail. Every
 * `#rating_mode_evap_pack_content .col-xs-1.nav.nav-tabs …`,
 * `#evap_pack_new …` and `.evap_pack-rating-mode.row …` rule in common.css is a
 * descendant selector on that shape — flatten it and they all silently stop
 * matching, with no console error.
 *
 * SMARTLIST (legacy :675-1055, config in evapSmartlist.ts): the `x / y · Reset
 * · Smartlist` strip on the Media and Pump tabs, and the catalogue panel on the
 * flip-card BACK of each of those columns. Counts load on open; items load only
 * while a panel is open. A pick REPLACES that type's params blob with the
 * catalogue row's fields and stores its id in `master_<type>_id`; narrowing the
 * filter to exactly one row auto-picks it. Legacy's `_cachedGet` + stateStorage
 * pair is RTK Query's URL cache here — same dedup and cross-open reuse.
 * Legacy declares fill/drain/overflow smartlists too but ships no UI for them,
 * so they are not ported (see evapSmartlist.ts).
 *
 * LIVE PREVIEW WITH PERSISTENCE (⚠ WRITES shared dev3): every model_field /
 * *_params_field change → staging.set → re-render. NOTHING is written until
 * SELECT, which PATCHes ahu/evap_packs/<id> with the CHANGED KEYS ONLY
 * (diffFromSaved). `.section_field` edits are the same silent local set
 * (legacy changeSectionField sets section {silent:true}).
 * draw:evap_pack:connection JSON-diff snapshot emitted on each staging change
 * (reuse useJsonDiffEmit / parity #334).
 *
 * ⚠ WRITE MODEL — write ONLY on Select, because that is what legacy does.
 * The legacy selector has no persist path at all: `grep -n "_scheduleUpdate\|
 * uni_tools.patch" evap_pack.selector.js` is empty. It mutates `staging_model`
 * locally and hands the result to `current_model` on Select
 * (`selectEvapPack` :1067-1106), and the FORM's auto-save turns that into one
 * `PATCH /api/v1.0/ahu/evap_packs/<id>` carrying the changed keys — e.g.
 * `{"fill_conn_hand_id":1,"drain_conn_hand_id":1}` for two Hand radios.
 *
 * This port originally invented a 500 ms debounced persist-on-change instead.
 * It was removed: it wrote before the user committed, it made Cancel unable to
 * undo (there was nothing left to undo), it raced its own responses, and it
 * meant Select itself sent no request — which is how "I changed the field, and
 * Select sends nothing" was reported. React now matches legacy request for
 * request.
 *
 * DATA-SAFETY (§4 + the 6e2a userArmed gate): NOTHING fires on mount / open /
 * tab-switch / field edit. Select is the ONLY mutating path and is still
 * change-gated by `userArmed` + a non-empty diff. Cancel performs no write at
 * all (legacy :1120-1127).
 *
 * SELECT  = current_model.set(staging.toJSON()) + form re-render + chassis hide.
 * CANCEL  = close only, no write (legacy cancelChanges). Both wired through
 *           the §30.4 chassis fan-out
 *           (evap_pack_<id>:selector:{select,cancel}).
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { airStreamOptions } from '../airStreamOptions';
import { useAirStreamOfferedIds } from '../useAirStreamOfferedIds';
import { useAppDispatch } from 'app/hooks';
import { store } from 'app/store';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { cmsList } from '../formKit';
import { catalogLabel, formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';
import SelectorNumberInput, { commitNumberValue } from './selectorNumberInput';
import { useJsonDiffEmit } from '../jsonDiffEmit';
import {
  useGetComponentDetailQuery,
  useGetComponentCmsDocQuery,
} from 'app/services/ahuComponentFormsApi';
import { useGetCmsAhuDocQuery } from 'app/services/ahuBoardApi';
import { ahuBoardFormsApi, type ICatalogRow } from 'app/services/ahuBoardFormsApi';
import { evapPackSelectorApi, usePatchEvapPackSelectorMutation } from './evapPackSelectorApi';
import { accessorySelectorApi, type ISmartlistItem } from './accessorySelectorApi';
import {
  EVAP_SMARTLIST_PAGE_SIZE,
  EVAP_SMARTLIST_TYPES,
  buildEvapSmartlistUrls,
  type EvapSmartlistType,
} from './evapSmartlist';
import type { ComponentFormShim } from '../componentFormTypes';
import type { SelectorTabProps } from './selectorTypes';
import { takeSectionPendingRerate } from '../../psychrometric/pendingRerate';
import { queueSectionPatch } from '../sectionPatchQueue';
import {
  SECTION_AIR_COMMIT_KEYS,
  commitSectionAirConditions,
  hasAnyKey,
  selectBoardSyncOn,
} from '../commitAirConditions';
import { cmsSeasonsByComponent, deriveSeasonValue } from '../cmsDoc';
import { seasonShowFlags } from '../SeasonGroup';
import type { ICmsAhuDoc } from 'models';
import type { IEvapPackRow } from './evapPackSelectorApi';

const EVAP_CMS_URL = '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/params/';

/**
 * The ten outputs `_fetchCalculate` writes back. Server-derived, so they are
 * never part of a write: legacy strips exactly this list before handing the
 * staging row to the form model (`selectEvapPack`, evap_pack.selector.js:1090).
 */
const CALCULATED_FIELDS = new Set([
  'media_aw_width',
  'media_aw_height',
  'sump_length',
  'face_velocity_actual',
  'face_velocity_std',
  'airflow_std',
  'media_apd',
  'media_clg_eff',
  'media_clg_eff_percent',
  'water_flow',
]);

type TabName = 'media' | 'construction' | 'pump' | 'options';

/** The legacy input rail (evap_pack.selector.tmpl:6-21) — 4 items, in order. */
const INPUT_TABS: Array<[TabName, string]> = [
  ['media', 'Media'],
  ['construction', 'Construction'],
  ['pump', 'Pump & Piping'],
  ['options', 'Options'],
];

/** Unwrap the {"evap_pack": {...}} CMS doc wrapper (mirrors useComponentForm). */
function unwrapCms(raw: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!raw) return {};
  const inner = raw.evap_pack;
  if (inner && typeof inner === 'object') return inner as Record<string, unknown>;
  const keys = Object.keys(raw);
  if (keys.length === 1 && raw[keys[0]] && typeof raw[keys[0]] === 'object') {
    return raw[keys[0]] as Record<string, unknown>;
  }
  return raw;
}

function parseParams(raw: unknown): Record<string, unknown> {
  if (raw == null || raw === '') return {};
  try {
    return JSON.parse(String(raw)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Legacy ifCompare '==' — option-selected match (loose; ids cross int/str). */
const eq = (a: unknown, b: unknown): boolean =>
  a != null && b != null && String(a) === String(b);

type ValveRow = { id: number; name: string } & Record<string, unknown>;

export const EvapPackSelector = ({
  modelId,
  ahu,
  activate,
  setSelectEnabled,
}: SelectorTabProps) => {
  // ★ Hoisted above every render-path return in this component: a hook
  // that is not reached on each render changes the hook count and React
  // throws #310. That broke the AHU board twice (e5f5bce64e, 3d655d33af),
  // both times past a clean type check.
  const offeredAirStreams = useAirStreamOfferedIds();
  // ---- read-only loads (fire on mount; never mutate) ----
  /**
   * `refetchOnMountOrArgChange` is what every OTHER selector already passes
   * here (coil :310, energy wheel :226, fan :362, gas heater :129, phx :188,
   * uvc :126) and this one alone omitted.
   *
   * `getComponentDetail` uses RTK Query's default 60 s `keepUnusedDataFor`
   * and its only tag is invalidated by the cascade refresh, nothing else — so
   * without this, closing the dialog and reopening within
   * a minute re-seeds staging from the PRE-EDIT row: every field just saved
   * looks reverted, which reads exactly like "it didn't save in the back end".
   * Refetching on open also picks up writes made by the FORM's own auto-save,
   * which a cache patch keyed to this selector's mutation would miss — and it
   * matches legacy, where opening the dialog always fetched the row.
   *
   * ⚠ `isFetching`, NOT `isLoading`. `isLoading` is true only on the FIRST ever
   * load, so with a cached entry it is false while the refetch is still in
   * flight and `data` is still the STALE row. Seeding off that is what made the
   * option above insufficient on its own: the refetch fired, but the seed had
   * already run against the pre-edit values and `seeded.current === modelId`
   * then blocked any re-seed when the fresh row landed.
   *
   * ⚠⚠ AND `isFetching` IS NOT ENOUGH EITHER — it is false for one render.
   * RTK Query starts the refetch from an effect inside the hook, so the FIRST
   * render after mount still reports `isFetching: false` alongside the stale
   * cached `data`; `isFetching` only flips true in the render after that. The
   * seed effect runs in that first commit, so it seeded from the pre-edit row
   * and latched `seeded.current` before the refetch was even visible.
   *
   * Measured on dev3, evap_pack 24202 (both Hand radios, 1 → 2): the reopen
   * GET returned `fill_conn_hand_id: 2` and the dialog still painted 1. The
   * value was correct in the database the whole time — only the dialog was
   * showing the row it had cached before the edit, which is exactly what
   * "clicking Select does not save the change" looks like from the outside.
   *
   * So gate on the fetch that settled AFTER this dialog opened:
   * `fulfilledTimeStamp` is stamped when a request fulfils, `openedAt` is
   * stamped on the first render, and a cached row from before the dialog
   * opened can never satisfy `fulfilledTimeStamp >= openedAt`. `isError` is an
   * escape hatch so a failed refetch shows the cached row rather than pinning
   * the preloader on for ever.
   */
  const {
    data: detail,
    isLoading: detailLoading,
    isFetching: detailFetching,
    isError: detailError,
    fulfilledTimeStamp,
  } = useGetComponentDetailQuery(
    { plural: 'evap_packs', id: modelId },
    { refetchOnMountOrArgChange: true, skip: !modelId }
  );
  const openedAt = useRef(Date.now());
  const detailSettled =
    detailError || (fulfilledTimeStamp != null && fulfilledTimeStamp >= openedAt.current);
  const { data: rawCms, isLoading: cmsLoading } =
    useGetComponentCmsDocQuery(EVAP_CMS_URL);
  const cms = useMemo(() => unwrapCms(rawCms), [rawCms]);
  // J6 — the GLOBAL cms doc (legacy self.globalCMS): season_by_component
  // ['Evap Pack'] feeds the Season select, and `air.air_stream` feeds the Air
  // Stream select — the evap CMS doc has NO `air` key at all (verified on
  // dev3: its keys are blank_off, drain, …, timeclock), which is why reading
  // air streams off it produced an empty list. Same fix, same reason, as
  // CoilSelector's ahuDoc. Keyed on the AHU's own manufacturer and
  // construction class, falling back to 1/1 for the header host, which has no
  // AHU (component_selector.popup.js:39).
  const ahuMfgId = Number(
    (ahu as Record<string, unknown> | null | undefined)?.mfg_id ??
      (ahu as Record<string, unknown> | null | undefined)?.manufacturer_id ??
      1
  );
  const ahuCcId = Number(
    (ahu as Record<string, unknown> | null | undefined)?.construction_class_id ?? 1
  );
  const { data: globalDoc } = useGetCmsAhuDocQuery({
    mfgId: ahuMfgId,
    constructionClassId: ahuCcId,
  });


  const [patchEvapPack] = usePatchEvapPackSelectorMutation();
  const emitIfChanged = useJsonDiffEmit();
  const dispatch = useAppDispatch();

  /**
   * Blank-Off options — legacy `fn['current.blank_off_list']`
   * (ahu/mvc/fn/map.js:1304), which resolves to the CASING LINER METAL SHEETS
   * catalogue (`section/collection/inner_liner`: `cms/<mfg>/v1.0.0/ahu/casing/
   * liner_metal_sheets/?page=1&construction_class_id=<n>`) — NOT
   * `cms.evap_pack.blank_off`. That key does exist on the evap doc, but it
   * holds `{selection:[…]}`, an OBJECT, so `cmsList` returned [] and the select
   * rendered with zero options — which is what "not openable" was.
   * `SectionForm.tsx:103-123` already loads the same list for the section
   * blank-off wall; this is the same call with the same params.
   */
  const [blankOffList, setBlankOffList] = useState<ICatalogRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    const sub = dispatch(
      ahuBoardFormsApi.endpoints.getCmsPaginated.initiate({
        mfgId: ahuMfgId,
        path: 'ahu/casing/liner_metal_sheets',
        page: 1,
        constructionClassId: ahuCcId,
      })
    );
    sub
      .unwrap()
      .then((res) => {
        if (!cancelled) setBlankOffList(res.results);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
  }, [ahuMfgId, ahuCcId, dispatch]);

  // ---- staging model (legacy: staging_model) ----
  // staging is a plain object held in a ref (the Backbone working copy); a
  // version counter drives re-render.
  const staging = useRef<IEvapPackRow>({});
  /** Last state the SERVER confirmed — the baseline every PATCH diffs against.
   *  Seeded from the GET, advanced only by a successful PATCH response. */
  const savedState = useRef<IEvapPackRow>({});
  // Dev-sync J6 — the SECTION draft pair (legacy staging_section_model +
  // _originalSection, evap_pack.selector.js:172-173): season_id + the four
  // per-season entering temps stage HERE, never on the component staging (a
  // section key on `staging` would leak into the component preview PATCH);
  // selectEvapPack diffs draft vs original and persists the changed keys.
  const sectionDraft = useRef<Record<string, unknown>>({});
  const originalSection = useRef<Record<string, unknown>>({});
  const seeded = useRef<number | null>(null);
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  /** The dialog root, so `autoSelectSingleOptionFields` (below) can sweep the
   *  same elements legacy's `self.$el.find('.input-part.evap_pack select')`
   *  does. All 24 of this file's selects are inside it and the output pane
   *  contributes none, so the scope matches even though dev's output block is
   *  a SIBLING of the root and ours is a child of it. */
  const rootRef = useRef<HTMLDivElement | null>(null);
  /** Last value `autoSelectSingleOptionFields` wrote per select, keyed by the
   *  store it targets — the loop guard described at that effect. */
  const autoPicked = useRef<Record<string, string>>({});

  // userArmed gate: the debounced PATCH fires ONLY after a real user change.
  // Nothing on mount / open / tab-switch arms it (data-safety — §4).
  const userArmed = useRef(false);

  // active tab — survives re-renders (legacy reads .input-nav-item.selected).
  const [activeTab, setActiveTab] = useState<TabName>('media');

  /**
   * Drift Eliminator on/off. This has to be its OWN state, not
   * `delim_depth_id != null` — legacy's checkbox is uncontrolled and ticking it
   * writes nothing to the model (`changeModelField` :521-529 only removes
   * `non-clickable` from the select). Deriving `checked` from the id meant that
   * on any AHU whose CMS offers more than one depth, ticking the box set no id,
   * the derived value stayed false, and the box snapped straight back — it
   * looked like it could not be ticked at all. Unticking still clears the id,
   * which is the half legacy does persist.
   */
  const [delimOn, setDelimOn] = useState(false);

  // ---- SMARTLIST (legacy _smartlist / _makeSmartlistState :129-141) ----
  // One independent state object per type. `data` and the counts live in React
  // state (they paint); `isOpen`, `page`, `hasMore`, `isLoading`, `reqId` and
  // `paramsChangedAutoPick` live in refs because the debounced loader reads
  // them at FIRE time, not at closure-build time — the same reason the
  // accessory selector keeps its `data-tab` in `dataTabRef`.
  const [smartlist, setSmartlist] = useState<
    Record<EvapSmartlistType, { data: ISmartlistItem[]; count: number | null; cmsCount: number | null }>
  >({
    media: { data: [], count: null, cmsCount: null },
    pump: { data: [], count: null, cmsCount: null },
  });
  const slOpen = useRef<Record<EvapSmartlistType, boolean>>({ media: false, pump: false });
  const slPage = useRef<Record<EvapSmartlistType, number>>({ media: 1, pump: 1 });
  const slHasMore = useRef<Record<EvapSmartlistType, boolean>>({ media: true, pump: true });
  const slLoading = useRef<Record<EvapSmartlistType, boolean>>({ media: false, pump: false });
  const slReqId = useRef<Record<EvapSmartlistType, number>>({ media: 0, pump: 0 });
  const slAutoPick = useRef<Record<EvapSmartlistType, boolean>>({ media: false, pump: false });
  const slTimers = useRef<
    Partial<Record<EvapSmartlistType, ReturnType<typeof setTimeout>>>
  >({});
  const calcTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Seed staging once the GET has SETTLED (re-seed if the id changes).
   *
   * The `detailFetching` guard is load-bearing, not defensive. With a warm
   * cache the first render already has `detail` — the PREVIOUS, pre-edit row —
   * while the refetch is still in flight. Seeding there captured stale values
   * into `staging` (and into the rollback snapshot), and `seeded.current` blocked the
   * re-seed when the real row arrived. Two consequences, both reported as
   * "it doesn't save":
   *   1. the dialog displayed the pre-edit values, and
   *   2. the snapshot the (now removed) Cancel rollback wrote back held them
   *      too, so cancelling actually REVERTED the record. Measured on
   *      evap_pack 24202: six fields saved correctly at 10:47 and were back
   *      at their pre-session values by 10:58.
   */
  useEffect(() => {
    if (!detail || detailFetching || !detailSettled) return;
    if (seeded.current === modelId) return;
    staging.current = { ...detail };
    savedState.current = { ...detail };
    // Dev-sync J5+J6 — the section draft pair seeds from the section row
    // (evap_pack.selector.js:172-173), then the chart's pending re-rate
    // handoff (js:185-202) merges on top: section-scoped gate; the bare
    // entering pair renames to `_summer` UNCONDITIONALLY and sets onto the
    // DETACHED draft (never the live section).
    const secSnap = {
      ...((activate.section as Record<string, unknown> | undefined) ?? {}),
    };
    originalSection.current = { ...secSnap };
    sectionDraft.current = { ...secSnap };
    const pendingFields = takeSectionPendingRerate(
      (activate.section as { id?: number } | undefined)?.id
    );
    if (pendingFields) {
      const f = pendingFields as Record<string, number | undefined>;
      if (f.eadb_temp !== undefined) {
        f.eadb_temp_summer = f.eadb_temp;
        f.eawb_temp_summer = f.eawb_temp;
        delete f.eadb_temp;
        delete f.eawb_temp;
      }
      Object.assign(sectionDraft.current, f);
    }
    seeded.current = modelId;
    // legacy renders `{{#if model.delim_depth_id}}checked{{/if}}` on open.
    setDelimOn(detail.delim_depth_id != null);
    bump();
  }, [detail, detailFetching, detailSettled, modelId, bump]);

  // checkButtonsAvailability (legacy): #rate non-clickable, #select ENABLED.
  // The chassis gates #select on setSelectEnabled — enable it immediately
  // (legacy removes #select.non-clickable in initialize, unconditionally).
  // NB: this does NOT arm userArmed and does NOT mark the chassis dirty —
  // opening must fire zero writes and leave the unsaved-guard disarmed until a
  // real field change (which calls markChanged → rating_mode:mounting:changed).
  useEffect(() => {
    setSelectEnabled(true);
  }, [setSelectEnabled]);

  // ---- _buildValveLists (automatic-valve display lists) ----
  const valveLists = useMemo(() => {
    const model = staging.current;
    const build = (
      idsKey: string,
      cmsParamsKey: string
    ): ValveRow[] => {
      const ids = model[idsKey];
      const params = cms[cmsParamsKey] as Record<string, unknown> | undefined;
      const all = (params?.automatic_valve as ValveRow[] | undefined) ?? [];
      if (!Array.isArray(ids)) return [];
      const out: ValveRow[] = [];
      (ids as number[]).forEach((id) => {
        const hit = all.find((v) => v.id === id);
        if (hit) out.push(hit);
      });
      return out;
    };
    return {
      fill: build('fill_automatic_valve_ids', 'fill_params'),
      drain: build('drain_automatic_valve_ids', 'drain_params'),
      overflow: build('overflow_automatic_valve_ids', 'overflow_params'),
    };
    // re-derive whenever staging version or cms changes (bump triggers it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cms, staging.current]);

  // ---- draw:evap_pack:connection emit (JSON-diff gated) ----
  useEffect(() => {
    if (seeded.current !== modelId) return;
    const m = staging.current;
    const payload = {
      fill: { conn_hand: m.fill_conn_hand_id, nipple: m.fill_id },
      drain: { conn_hand: m.drain_conn_hand_id, nipple: m.drain_id },
      overflow: { nipple: m.overflow_id },
    };
    const sectionId = Number(m.section_id);
    emitIfChanged(`evap_sel:${modelId}`, payload, () => {
      ahuBoardBus.emit('draw:evap_pack:connection', payload, sectionId);
    });
  });

  /**
   * Diff of the staging row against the last state the SERVER confirmed —
   * the payload for every write this selector makes.
   *
   * ⚠ This used to send the whole staging row, and consequently the dialog
   * never saved anything at all. `tag` is a non-blankable CharField on
   * AHUEvapPackSerializer and the row's tag is `""`, so echoing the GET body
   * back always failed validation. Measured on dev3, evap_pack 24202:
   *
   *   PATCH <full GET body>          -> 400 {"tag":["This field may not be blank."]}
   *   PATCH <same body minus tag>    -> 200
   *   PATCH {"airflow_actual": 5000} -> 200
   *
   * Sending only changed keys is what `autoSaveModel` already does for
   * ordinary form fields (§49.2 "save → PATCH {changed only}"), and it makes
   * the request immune to any other field the serializer will not round-trip,
   * not just `tag`.
   *
   * The ten `_fetchCalculate` outputs are excluded: they are server-derived,
   * and legacy strips exactly this list before writing back (`selectEvapPack`
   * CALCULATED_FIELDS, evap_pack.selector.js:1090-1095).
   *
   * Key set = the UNION of the baseline and the staging row, which is what
   * legacy's `evapChanged` walks (`Object.keys(Object.assign({}, self.init,
   * currentData))`, :1078-1084), and it skips `id` for the same reason legacy
   * does. Walking only `Object.keys(staging.current)` missed a key that the
   * baseline HAS and staging no longer does — a delete rather than a change.
   * Nothing in this dialog deletes keys today (clearing a select writes null,
   * `onModelField` :595-598), so this is closing a hole rather than fixing a
   * live symptom, but the union is what legacy compares and it costs nothing.
   *
   * Note the shapes are not otherwise equivalent, and it matters when reading
   * legacy: `evapChanged` is a BOOLEAN gate. When it is true legacy hands the
   * WHOLE staging row to `current_model.set(...)`, and the form model's
   * auto-save is what reduces that to the changed keys it PATCHes. This
   * function plays the second role, not the first — it is the port of that
   * reduction, which is why it returns a payload instead of a flag.
   */
  const diffFromSaved = useCallback((): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    const base = savedState.current;
    const keys = new Set([...Object.keys(base), ...Object.keys(staging.current)]);
    keys.forEach((k) => {
      if (k === 'id' || CALCULATED_FIELDS.has(k)) return;
      if (
        JSON.stringify(staging.current[k] ?? null) !== JSON.stringify(base[k] ?? null)
      ) {
        out[k] = staging.current[k] ?? null;
      }
    });
    /**
     * STICKY air stream — `air_stream_id` must never travel alone.
     * `AHUEvapPackViewSet.partial_update` (epack_viewset.py:341-354) does:
     *
     *   if 'air_stream_id' in request.data and ... is not None:
     *       ...
     *       if 'airflow_actual' not in request.data:
     *           evap_pack.airflow_actual = evap_pack.get_airflow(air_stream)
     *
     * i.e. it RECOMPUTES the airflow from the section whenever the stream
     * changes and the request does not pin it. Measured on dev3, evap_pack
     * 24202: `PATCH {"air_stream_id": 6}` returned 200 and silently reset a
     * user-entered `airflow_actual` of 12500 to null.
     *
     * That was unreachable while the whole row was posted (airflow was always
     * present) and is unreachable in legacy, which never PATCHes from the
     * selector at all — the diff-only payload is what exposes it.
     * `autoSaveModel.ts:375-382` carries the identical sticky for coils
     * ("air_stream_id w/o airflow_actual drags airflow_actual+airflow_std"),
     * so this is the house fix for the same server behaviour. Only
     * `airflow_actual` is pinned here: unlike the coil model, AHUEvapPack has
     * no `airflow_std` column (it is derived), so there is nothing to send.
     */
    if (out.air_stream_id != null && !('airflow_actual' in out)) {
      out.airflow_actual = staging.current.airflow_actual ?? null;
    }
    return out;
  }, []);


  // mark a real user change: arm persist + tell the chassis (makeChanges tick).
  const markChanged = useCallback(() => {
    if (!userArmed.current) {
      userArmed.current = true;
    }
    // chassis unsaved-guard: emit EVERY change (legacy delegates change on the
    // chassis). selectEnabled is already true so makeChanges latches hasChanges.
    ahuBoardBus.emit('rating_mode:mounting:changed');
  }, []);

  /**
   * `_fetchCalculate` (evap_pack.selector.js:960-1001) — 300 ms debounced GET
   * that recomputes the ten derived outputs. Silent: it writes staging and
   * re-renders but never arms `userArmed` and never schedules a PATCH, because
   * the values it returns are server-derived, not user input (legacy sets them
   * `{silent:true}` then calls `render_output()` only). `selectEvapPack` strips
   * the same ten fields before writing back, so they never round-trip.
   */
  const fetchCalculate = useCallback(() => {
    const sectionId = sectionDraft.current.id;
    const mediaId = staging.current.master_media_id;
    const airflow = staging.current.airflow_actual;
    if (!sectionId || !mediaId || !airflow) return;
    if (calcTimer.current) clearTimeout(calcTimer.current);
    calcTimer.current = setTimeout(() => {
      const params = new URLSearchParams({
        section_id: String(sectionId),
        master_media_id: String(mediaId),
        airflow_actual: String(airflow),
      });
      const eadb = sectionDraft.current.eadb_temp_summer;
      const eawb = sectionDraft.current.eawb_temp_summer;
      if (eadb != null) params.append('eadb_temp', String(eadb));
      if (eawb != null) params.append('eawb_temp', String(eawb));
      void dispatch(
        evapPackSelectorApi.endpoints.getEvapPackCalculate.initiate(params.toString())
      )
        .unwrap()
        .then((data) => {
          if (!data || typeof data !== 'object') return;
          staging.current = { ...staging.current, ...data };
          bump();
        })
        .catch(() => undefined);
    }, 300);
  }, [dispatch, bump]);

  // loadSmartlist is declared below (it needs applySmartlistItem, which needs
  // the field handlers). The handlers reach it through a ref — naming it in
  // their dep arrays would be a TDZ error. Same shape as the accessory
  // selector's fetchBladeOrientationsRef.
  const loadSmartlistRef = useRef<((type: EvapSmartlistType) => void) | null>(null);

  // ---- field handlers (legacy changeModelField / changeSectionField /
  //      changeParamsField) ----
  const onModelField = useCallback(
    (name: string, rawValue: string | boolean) => {
      let value: unknown = rawValue;
      if (typeof rawValue === 'string') {
        if (name.endsWith('_id') && rawValue !== '' && rawValue !== 'None') {
          value = parseInt(rawValue, 10) || null;
        } else if (rawValue === 'None' || rawValue === '') {
          value = null;
        }
      }
      staging.current = { ...staging.current, [name]: value };
      markChanged();
      bump();
      // legacy changeModelField :543 — these two feed the calculate endpoint.
      if (name === 'airflow_actual' || name === 'master_media_id') fetchCalculate();
    },
    [markChanged, bump, fetchCalculate]
  );

  const onParamsField = useCallback(
    (paramsKey: string, name: string, rawValue: string) => {
      const params = parseParams(staging.current[paramsKey]);
      if (rawValue === 'None' || rawValue === '') {
        delete params[name];
      } else {
        const n = Number(rawValue);
        params[name] = rawValue !== '' && !Number.isNaN(n) ? n : rawValue;
      }
      staging.current = {
        ...staging.current,
        [paramsKey]: JSON.stringify(params),
      };
      markChanged();
      bump();
      // legacy changeParamsField :630-633 — a params edit re-filters the
      // matching smartlist AND arms the auto-pick, so narrowing the filter down
      // to a single catalogue row selects it for you.
      const type = paramsKey.replace('_params', '') as EvapSmartlistType;
      if (EVAP_SMARTLIST_TYPES[type]) {
        slAutoPick.current[type] = true;
        loadSmartlistRef.current?.(type);
      }
    },
    [markChanged, bump]
  );

  // ---- smartlist controller (legacy :675-1055) ----

  /**
   * `selectFromSmartlist` (:1019-1055) — a picked catalogue row REPLACES the
   * type's params blob with just `cfg.paramFields` (not a merge) and stores the
   * row id in the master field. Closes the panel; media additionally recalculates.
   */
  const applySmartlistItem = useCallback(
    (type: EvapSmartlistType, id: number, items: ISmartlistItem[]) => {
      const cfg = EVAP_SMARTLIST_TYPES[type];
      const item = items.find((i) => i.id === id);
      const next: Record<string, unknown> = { [cfg.masterField]: id };
      if (item) {
        const params: Record<string, unknown> = {};
        cfg.paramFields.forEach((f) => {
          if (item[f] != null) params[f] = item[f];
        });
        next[cfg.paramsKey] = JSON.stringify(params);
      }
      staging.current = { ...staging.current, ...next };
      slOpen.current[type] = false;
      setSmartlist((s) => ({ ...s, [type]: { ...s[type], count: 1 } }));
      markChanged();
      bump();
      if (type === 'media') fetchCalculate();
    },
    [markChanged, bump, fetchCalculate]
  );

  /**
   * `loadSmartlist` (:713-834) — 300 ms debounced, reqId-guarded. Items are
   * fetched ONLY while the panel is open (counts always are), which is why
   * `slOpen` is a ref: the toggle sets it synchronously on the line before it
   * calls this, and the debounce body must see the POST-toggle value.
   * RTK Query's URL cache + in-flight dedup stands in for legacy's
   * `_cachedGet` + `stateStorage` pair — same semantic, one mechanism.
   */
  const loadSmartlist = useCallback(
    (type: EvapSmartlistType) => {
      const timer = slTimers.current[type];
      if (timer) clearTimeout(timer);
      slTimers.current[type] = setTimeout(() => {
        const params = parseParams(staging.current[EVAP_SMARTLIST_TYPES[type].paramsKey]);
        const { itemsUrl, countUrl, totalUrl } = buildEvapSmartlistUrls(type, params);
        slPage.current[type] = 1;
        slHasMore.current[type] = true;
        slLoading.current[type] = true;
        const reqId = ++slReqId.current[type];
        const isOpen = slOpen.current[type];
        void Promise.all([
          isOpen
            ? dispatch(
                accessorySelectorApi.endpoints.getSmartlistPage.initiate(itemsUrl)
              ).unwrap()
            : Promise.resolve(null),
          dispatch(
            accessorySelectorApi.endpoints.getSmartlistCount.initiate(countUrl)
          ).unwrap(),
          dispatch(
            accessorySelectorApi.endpoints.getSmartlistCount.initiate(totalUrl)
          ).unwrap(),
        ])
          .then(([items, count, total]) => {
            if (reqId !== slReqId.current[type]) return; // stale
            slLoading.current[type] = false;
            const newItems = Array.isArray(items) ? items : null;
            setSmartlist((s) => ({
              ...s,
              [type]: {
                data: newItems ?? s[type].data,
                count,
                cmsCount: total,
              },
            }));
            if (newItems) slHasMore.current[type] = newItems.length === EVAP_SMARTLIST_PAGE_SIZE;
            // auto-pick (:797-825): exactly one match, and either the dialog
            // opened with nothing picked or the user just narrowed the filter.
            const cfg = EVAP_SMARTLIST_TYPES[type];
            const currentMasterId = staging.current[cfg.masterField];
            const openAutoPick = !currentMasterId && count === 1;
            const paramsAutoPick = slAutoPick.current[type];
            if (count === 1 && (openAutoPick || paramsAutoPick)) {
              slAutoPick.current[type] = false;
              void dispatch(
                accessorySelectorApi.endpoints.getSmartlistPage.initiate(itemsUrl)
              )
                .unwrap()
                .then((data) => {
                  if (
                    Array.isArray(data) &&
                    data.length === 1 &&
                    data[0].id !== currentMasterId
                  ) {
                    applySmartlistItem(type, data[0].id, data);
                  }
                })
                .catch(() => undefined);
            } else {
              slAutoPick.current[type] = false;
            }
          })
          .catch(() => {
            slLoading.current[type] = false;
          });
      }, 300);
    },
    [dispatch, applySmartlistItem]
  );
  loadSmartlistRef.current = loadSmartlist;

  /** `loadMoreSmartlist` (:838-871) — infinite scroll, `page=N`, 50/page. */
  const loadMoreSmartlist = useCallback(
    (type: EvapSmartlistType) => {
      if (slLoading.current[type] || !slHasMore.current[type]) return;
      const nextPage = slPage.current[type] + 1;
      const params = parseParams(staging.current[EVAP_SMARTLIST_TYPES[type].paramsKey]);
      const { itemsUrl } = buildEvapSmartlistUrls(type, params, nextPage);
      slLoading.current[type] = true;
      void dispatch(accessorySelectorApi.endpoints.getSmartlistPage.initiate(itemsUrl))
        .unwrap()
        .then((items) => {
          slLoading.current[type] = false;
          const newItems = Array.isArray(items) ? items : [];
          if (!newItems.length) {
            slHasMore.current[type] = false;
            return;
          }
          slPage.current[type] = nextPage;
          slHasMore.current[type] = newItems.length === EVAP_SMARTLIST_PAGE_SIZE;
          setSmartlist((s) => ({
            ...s,
            [type]: { ...s[type], data: s[type].data.concat(newItems) },
          }));
        })
        .catch(() => {
          slLoading.current[type] = false;
        });
    },
    [dispatch]
  );

  /** `showSmartlist` (:693-711) — flips the card and loads on open. */
  const toggleSmartlist = useCallback(
    (type: EvapSmartlistType) => {
      slOpen.current[type] = !slOpen.current[type];
      bump();
      if (slOpen.current[type]) loadSmartlist(type);
    },
    [bump, loadSmartlist]
  );

  /** `resetParams` (:1003-1017) — clears the params blob and the master id. */
  const resetSmartlist = useCallback(
    (type: EvapSmartlistType) => {
      const cfg = EVAP_SMARTLIST_TYPES[type];
      staging.current = {
        ...staging.current,
        [cfg.paramsKey]: '{}',
        [cfg.masterField]: null,
      };
      slAutoPick.current[type] = false;
      markChanged();
      bump();
      loadSmartlist(type);
    },
    [markChanged, bump, loadSmartlist]
  );

  // `_loadCounts` (:649-673) — the initial badge fetch, once the staging model
  // is seeded. Counts only; no item request, because nothing is open yet.
  //
  // The deps MIRROR the seeding effect's rather than watching `detail` alone,
  // and that is load-bearing on the SECOND open. This effect has to fire on the
  // commit that seeds `staging` (it reads `staging.current[paramsKey]` to build
  // the count URLs), and it is `detail` settling that triggers that commit — but
  // on a reopen `detail` is already in the RTKQ cache, and RTK Query's
  // structuralSharing (on by default) hands back the SAME object reference when
  // the mount refetch comes back deep-equal. So `[detail, modelId]` never
  // changed after mount: the effect ran once, bailed on `seeded.current` (still
  // null, the refetch was in flight), and never ran again. Both badges stayed
  // `count: null`, which SmartlistStrip renders as a warning-red "  /  " — the
  // reported "counts are empty after closing and reopening". `detailFetching`
  // and `detailSettled` both flip on that commit whether or not the payload
  // changed, so keying on them re-runs it exactly when the seed lands.
  const countsLoaded = useRef<number | null>(null);
  useEffect(() => {
    if (!modelId || seeded.current !== modelId) return;
    if (countsLoaded.current === modelId) return;
    countsLoaded.current = modelId;
    (Object.keys(EVAP_SMARTLIST_TYPES) as EvapSmartlistType[]).forEach((t) =>
      loadSmartlist(t)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, detailFetching, detailSettled, modelId]);

  // section_field: SILENT local set on the SECTION DRAFT, NO scheduled PATCH
  // (legacy changeSectionField, evap_pack.selector.js:568-583: stages on
  // staging_section_model {silent:true}; selectEvapPack persists the diff).
  // J6 — decimal inputs clamp per legacy validateNumberField (data-min 8 /
  // data-max 120, 2 decimals).
  const onSectionField = useCallback(
    (name: string, rawValue: string, minV = 8, maxV = 120): number | null => {
      const raw = parseFloat(String(rawValue).replace(/,/g, ''));
      let value: number | null = Number.isNaN(raw) ? null : raw;
      if (value != null) {
        value = Math.min(Math.max(value, minV), maxV);
        value = Number(value.toFixed(2));
      }
      sectionDraft.current = { ...sectionDraft.current, [name]: value };
      markChanged();
      bump();
      return value;
    },
    [markChanged, bump]
  );

  // J6 — changeSeasonField (evap_pack.selector.js:597-609): season_id +
  // (derived) season_value onto the draft, full re-render.
  const onSeasonField = useCallback(
    (raw: string) => {
      sectionDraft.current = {
        ...sectionDraft.current,
        season_id: parseInt(raw, 10) || null,
      };
      markChanged();
      bump();
    },
    [markChanged, bump]
  );

  /**
   * `autoSelectSingleOptionFields` (evap_pack.selector.js:475-487) — the last
   * statement of every legacy `render()` (:472), and never ported. Any select
   * in the input pane whose catalogue offers exactly ONE real option is picked
   * for the user, and the pick is dispatched as an ordinary change, so it lands
   * on the staging model and ticks the chassis just like a typed edit.
   *
   * TWO reported symptoms are this one routine. Measured against this AHU's CMS
   * (`/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/params/`, mfg 1):
   *   - `media.manufacturer` holds ONE row (81 Munters) — dev re-picks it after
   *     the Media smartlist Reset blanks the params blob; we left it on "- -".
   *   - `freez_stat` holds ONE row (1 Johnson-Controls_A19BA-40C) — dev fills
   *     the Options tab's Freez-Stat on open; we left it on "- -".
   * `timeclock` holds TWO, which is why Time Clock reads "- -" on BOTH boxes —
   * the corroborating negative that says the rule is sole-option, not a stored
   * default. Neither field is touched by the smartlist row-select
   * (`selectFromSmartlist` :1019-1055 writes only the master id + `paramFields`),
   * so nothing else in the port could have filled them.
   *
   * Ported as a DOM sweep rather than a transcribed field list because a sweep
   * is what legacy IS — "every select except edge_finish_id", 24 of them here.
   * A hand-kept table would be a fresh instance of the dropped-tail class (the
   * `HAS_RATING` transcription that lost `evap_pack`) on every future JSX row.
   * We only READ the DOM: the write goes through the same handlers the onChange
   * props use, because assigning `.value` on a controlled select is a silent
   * no-op — the `attr("checked")` finding, same shape.
   *
   * No dep array, exactly like legacy's placement at the tail of render(). It
   * settles in one extra pass: the follow-up run sees `select.value` already
   * equal to the sole option and writes nothing.
   *
   * Arming the PATCH via `markChanged()` is deliberate and is parity, not a
   * leak. Dev's Select gate is `evapChanged` (:1077-1084), a diff of the
   * staging row against `self.init` — an auto-picked value makes that true, so
   * dev writes it on SELECT even when the user touched nothing else. Cancel
   * still writes nothing on either box.
   */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.querySelectorAll('select').forEach((select) => {
      // Legacy's one exclusion (:477): edge finish is left to the smartlist.
      if (select.name === 'edge_finish_id') return;
      // Legacy's filter (:479-481) — the "- -" placeholder is `value="None"`,
      // and a select React could not match renders `value=""`.
      const real = Array.from(select.options).filter(
        (o) => o.value !== '' && o.value !== 'None'
      );
      if (real.length !== 1) return;
      const value = real[0].value;

      // Which store this select reads is what decides the handler. Class alone
      // cannot say: dev's template puts BOTH `<type>_params_field` and
      // `model_field` on four of them (tmpl:499/533/554/567) and we mirror it,
      // but three of those bind the params blob and `drain_automatic_valve_id`
      // binds the column. Route to whatever this row's own onChange calls, so
      // an auto-pick is indistinguishable from the user clicking the option.
      const classes = select.className.split(/\s+/);
      const paramsClass = classes.find((c) =>
        /^(media|pump|fill|drain|overflow)_params_field$/.test(c)
      );
      const useParams = Boolean(paramsClass) && select.name !== 'drain_automatic_valve_id';
      const key = `${useParams ? paramsClass : 'model'}.${select.name}`;

      if (select.value === value) {
        // Converged. Forget the attempt so blanking the field re-picks it,
        // which is what dev does — its sweep has no memory either.
        delete autoPicked.current[key];
        return;
      }
      // A write that does not move the select would re-fire this effect
      // forever. That can only happen if the routing above stops matching the
      // JSX, so refuse to repeat an attempt rather than hang the tab.
      if (autoPicked.current[key] === value) return;
      autoPicked.current[key] = value;

      if (useParams && paramsClass) {
        onParamsField(paramsClass.replace('_params_field', '_params'), select.name, value);
      } else if (classes.includes('model_field')) {
        onModelField(select.name, value);
      } else if (classes.includes('sec_field')) {
        // `season_id` is the only `.sec_field` SELECT; dev fires both
        // changeSectionField and changeSeasonField for it (:148-149) and the
        // former is a no-op on a non-numeric select, so the pair reduces to one.
        onSeasonField(value);
      }
    });
  });

  // ---- Select / Cancel via the §30.4 chassis fan-out ----
  // current_model = the live evap_pack form model via window.__FORM_SHIM__[id]
  // (the Q15 shim: .model.set + .render = legacy current_model.set + view.render).
  const getFormShim = (): ComponentFormShim | undefined =>
    window.__FORM_SHIM__?.[modelId];

  // selectEvapPack: section diff persist + current_model.set(staging.toJSON())
  // + view.render + hide (evap_pack.selector.js:1067-1106 — section FIRST).
  useEffect(() => {
    const key =
      `evap_pack_${modelId}:selector:select` as `${string}_${number}:selector:select`;
    const off = ahuBoardBus.on(key, () => {
      // Dev-sync J6 — `if (sectionChanged) self.section.set(staging_section
      // _model.toJSON())` (js:1085-1087): Backbone set computes the CHANGED
      // keys and the section model's autosave runs the Checkpoint-4 gate
      // (section.js:200-284) — an EVAP PACK section whose change-set carries
      // the _summer entering pair while synchronize is on routes through
      // POST evap_packs/<id>/commit_air_conditions (the epack's own
      // endpoint, :268-283); anything else is the plain PATCH, whose
      // success fires the changed:section event for a changed _summer pair
      // (:231-234).
      {
        const sec = originalSection.current as {
          id?: number;
          tunnel_id?: number;
        } & Record<string, unknown>;
        const draft = sectionDraft.current;
        const changes: Record<string, unknown> = {};
        (
          [
            'season_id',
            'eadb_temp_summer',
            'eawb_temp_summer',
            'eadb_temp_winter',
            'eawb_temp_winter',
          ] as const
        ).forEach((k) => {
          const next = k === 'season_id' ? Number(draft[k]) || null : draft[k] ?? null;
          if (String(next ?? '') !== String(sec[k] ?? '')) changes[k] = next;
        });
        if (sec.id != null && Object.keys(changes).length) {
          const sectionId = sec.id;
          const syncOn = selectBoardSyncOn(store.getState());
          if (syncOn && hasAnyKey(changes, SECTION_AIR_COMMIT_KEYS)) {
            void commitSectionAirConditions({
              dispatch,
              url: `/api/v1.0/ahu/evap_packs/${modelId}/commit_air_conditions`,
              sectionId,
              tunnelId: Number(sec.tunnel_id),
              changes,
            });
          } else {
            const summerEnteringChanged =
              'eadb_temp_summer' in changes || 'eawb_temp_summer' in changes;
            queueSectionPatch({
              dispatch,
              sectionId,
              tunnelId: Number(sec.tunnel_id),
              changes,
              onSuccess: () => {
                if (summerEnteringChanged) {
                  ahuBoardBus.emit(`changed:section:${sectionId}:eadb_temp:eawb_temp`);
                }
              },
            });
          }
        }
      }
      // THE component write — the only one this dialog makes, and the port of
      // legacy's `current_model.set(data)` → form auto-save → one PATCH with
      // the changed keys (evap_pack.selector.js:1086-1097). The form model
      // cannot make that write on React's behalf: its §49.2 guard table bails
      // out while `viewRef.rate` is set, which it is for the whole life of the
      // rating dialog. So the selector sends it, with the same body legacy
      // sends — `diffFromSaved()` against the row as opened.
      if (userArmed.current) {
        const pending = diffFromSaved();
        if (Object.keys(pending).length) {
          void patchEvapPack({ id: modelId, data: pending })
            .unwrap()
            .catch((err) => {
              // eslint-disable-next-line no-console
              console.error('[evap_pack selector] select flush failed', err);
            });
        }
      }
      const shim = getFormShim();
      if (shim) {
        const data = { ...staging.current };
        delete (data as { id?: unknown }).id;
        shim.model.set(data);
        shim.render();
      }
      ahuBoardBus.emit('rating_mode:hide');
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, dispatch, diffFromSaved, patchEvapPack]);

  /**
   * cancelChanges — CLOSE ONLY. No write, and now genuinely an UNDO again.
   *
   * Legacy is unambiguous (evap_pack.selector.js:1120-1127):
   *
   *   cancelChanges: async function (e) {
   *     self._clearTimers(); self.remove(); self.stopListening();
   *     self.undelegateEvents();
   *     if (e) Dispatcher.trigger('rating_mode:hide');
   *   }
   *
   * — it performs no PATCH, because the legacy selector never persisted
   * anything to begin with; there was nothing to undo.
   *
   * The port once had a rollback here (PATCH the `init` snapshot back), invented
   * alongside the invented persist-on-change and removed as a data-loss weapon:
   * it wrote whatever `init` happened to hold, so any defect that seeded `init`
   * wrong turned Cancel into "silently revert the record". Removing it left
   * Cancel meaning "stop editing" rather than "undo", because the edits were
   * already on the server.
   *
   * Now that nothing is written before Select, both halves are legacy again:
   * Cancel writes nothing AND discards nothing that was ever written. There is
   * no listener left to register — the calculate and smartlist timers legacy
   * clears in `_clearTimers` are cleared by the unmount cleanup below, and
   * `rating_mode:hide` unmounts us on either button.
   */

  // unmount cleanup — legacy _clearTimers (:1059-1065) clears the calculate
  // timer and every per-type smartlist timer.
  useEffect(
    () => () => {
      if (calcTimer.current) clearTimeout(calcTimer.current);
      Object.values(slTimers.current).forEach((t) => t && clearTimeout(t));
    },
    []
  );

  // The preloader stays up until the on-open refetch has SETTLED — not merely
  // until `isFetching` reads false, which it also does for the one render
  // before RTK Query has started the refetch (see `detailSettled` above).
  if (
    detailLoading ||
    detailFetching ||
    !detailSettled ||
    cmsLoading ||
    seeded.current !== modelId
  ) {
    return <div className="preloader dotted-preloader active" />;
  }

  const model = staging.current;
  const mediaParams = parseParams(model.media_params);
  const pumpParams = parseParams(model.pump_params);
  const fillParams = parseParams(model.fill_params);
  const drainParams = parseParams(model.drain_params);
  const overflowParams = parseParams(model.overflow_params);

  // ---- J6 — the season derivations (evap_pack.selector.js:367-392) ----
  // Which option the Season select shows + which value the data-season CSS
  // gate uses when section.season_id is unset: Evap Pack's own default is
  // "Summer" (resolved from the CMS rows BY VALUE) — NOT AHUSection.
  // season_value's component-agnostic 'summer_winter' fallback.
  const evapSeasons = cmsSeasonsByComponent(
    globalDoc as ICmsAhuDoc | undefined,
    'Evap Pack'
  );
  const draftSeasonId = Number(sectionDraft.current.season_id) || null;
  const defaultSeasonOption = evapSeasons.find((s2) => s2.value === 'summer');
  const seasonSelectedId =
    draftSeasonId ?? (defaultSeasonOption ? Number(defaultSeasonOption.id) : null);
  const pickedSeason = evapSeasons.find((s2) => Number(s2.id) === seasonSelectedId);
  const seasonDisplayValue =
    draftSeasonId != null
      ? deriveSeasonValue(globalDoc as ICmsAhuDoc | undefined, draftSeasonId)
      : typeof pickedSeason?.value === 'string'
        ? pickedSeason.value
        : 'summer';
  const { showSummer, showWinter } = seasonShowFlags(seasonSelectedId);

  // _computeLeavingTemps (js:333-365): summer leaving derives from the staged
  // summer entering + media_clg_eff (fallback = entering when clg_eff can't
  // compute yet); winter media is dormant — leaving mirrors entering.
  const leaving = (() => {
    const d = sectionDraft.current;
    const eadb = parseFloat(String(d.eadb_temp_summer ?? ''));
    const eawb = parseFloat(String(d.eawb_temp_summer ?? ''));
    const clgEff = parseFloat(String(model.media_clg_eff ?? ''));
    const lawbTemp = !Number.isNaN(eawb) ? eawb : null;
    const ladbTemp =
      !Number.isNaN(eadb) && !Number.isNaN(eawb) && !Number.isNaN(clgEff)
        ? Math.round((eadb - (eadb - eawb) * clgEff) * 100) / 100
        : null;
    const eadbWinter = parseFloat(String(d.eadb_temp_winter ?? ''));
    const eawbWinter = parseFloat(String(d.eawb_temp_winter ?? ''));
    return {
      ladb_temp_summer_effective:
        ladbTemp !== null ? ladbTemp : !Number.isNaN(eadb) ? eadb : null,
      lawb_temp_summer_effective: lawbTemp,
      ladb_temp_winter_effective: !Number.isNaN(eadbWinter) ? eadbWinter : null,
      lawb_temp_winter_effective: !Number.isNaN(eawbWinter) ? eawbWinter : null,
    };
  })();

  // get_one_decimal (handlebars helpers.js:154-157): `+v` then
  // `res ? res.toFixed(2) : ''` — the falsy-zero quirk preserved.
  const oneDec = (v: unknown): string => {
    const n = Number(v);
    return n ? n.toFixed(2) : '';
  };

  // `air_stream` off the AHU cms doc — `const air_stream = (globalCmsData.air &&
  // globalCmsData.air.air_stream) || []` (evap_pack.selector.js:370). It was
  // being read off the EVAP doc, which has no `air` key, so the select was
  // always empty.
  // Offered by this AHU's construction class, plus whatever the record
  // already holds -- same rule as the accessory dialogs.
  const airStream = airStreamOptions(
    cmsList(globalDoc as Record<string, unknown> | undefined, 'air.air_stream'),
    offeredAirStreams,
    model.air_stream_id as number | string | null | undefined
  );

  // option helpers
  // option list builder — the parent <select> is CONTROLLED via `value`
  // (so no `selected` attr on options; eq() drives the controlled value match
  // upstream via sel()).
  const opt = (
    rows: Array<Record<string, unknown>>,
    _value: unknown,
    labelKey: string,
    valueKey = 'id'
  ) =>
    rows.map((r) => (
      <option key={String(r[valueKey])} value={String(r[valueKey])}>
        {String(r[labelKey] ?? '')}
      </option>
    ));

  const sel = (value: unknown) =>
    value == null || value === '' ? 'None' : String(value);

  /** Legacy output lookup — first row whose <key> equals <value>, via <labelKey>. */
  const look = (
    rows: Array<Record<string, unknown>>,
    key: string,
    value: unknown,
    labelKey: string
  ): string => {
    const row = rows.find((r) => eq(r[key], value));
    return row ? String(row[labelKey] ?? '') : '';
  };

  // ---- output summary strings (evap_pack.selector.output.tmpl:11-180) ----
  const outMedia = `${look(cmsList(cms, 'media.manufacturer'), 'id', mediaParams.mfg_id, 'name')} ${look(cmsList(cms, 'media.model'), 'id', mediaParams.model_id, 'name')}`;
  const outDelim = look(
    cmsList(cms, 'drift_eliminator.depth'),
    'id',
    model.delim_depth_id,
    'value'
  );
  const outWetParts = look(
    cmsList(cms, 'drip_tray_mtl_gauge'),
    'id',
    model.drip_tray_mtl_gauge_id,
    'model'
  );
  // tmpl:112 — mfg then optional ", model", ", <power> hp", ", <voltage>". The
  // leading-comma-when-mfg-is-blank wart is legacy's; kept verbatim.
  const outPump =
    look(cmsList(cms, 'pump.manufacturer'), 'id', pumpParams.mfg_id, 'name') +
    (pumpParams.model ? `, ${String(pumpParams.model)}` : '') +
    (pumpParams.power ? `, ${String(pumpParams.power)} hp` : '') +
    (pumpParams.voltage ? `, ${String(pumpParams.voltage)}` : '');
  const outPiping = look(cmsList(cms, 'piping'), 'id', model.pipe_mtl_sch_id, 'name');
  // tmpl:127 — nipple, then valve (comma only when a nipple precedes it), then hand.
  const fillNipple = look(cmsList(cms, 'fill'), 'nipple_id', fillParams.nipple_id, 'nipple');
  const fillValve = model.fill_automatic_valve_id
    ? look(valveLists.fill, 'id', model.fill_automatic_valve_id, 'name')
    : '';
  const fillHand = look(
    cmsList(cms, 'fill_params.connection_hand'),
    'id',
    model.fill_conn_hand_id,
    'name'
  );
  const outFill =
    fillNipple +
    (fillValve ? `${fillParams.nipple_id ? ', ' : ''}${fillValve}` : '') +
    (fillHand ? `, ${fillHand}` : '');
  // tmpl:140 — drain reverses it: valve first, then nipple, then hand.
  const drainValve = model.drain_automatic_valve_id
    ? look(valveLists.drain, 'id', model.drain_automatic_valve_id, 'name')
    : '';
  const drainNipple = look(
    cmsList(cms, 'drain'),
    'nipple_id',
    drainParams.nipple_id,
    'nipple'
  );
  const drainHand = look(
    cmsList(cms, 'drain_params.connection_hand'),
    'id',
    model.drain_conn_hand_id,
    'name'
  );
  const outDrain =
    drainValve +
    (drainNipple ? `${model.drain_automatic_valve_id ? ', ' : ''}${drainNipple}` : '') +
    (drainHand ? `, ${drainHand}` : '');
  const outOverflow = look(
    cmsList(cms, 'overflow'),
    'nipple_id',
    overflowParams.nipple_id,
    'nipple'
  );
  const outAirStream = look(airStream, 'id', model.air_stream_id, 'name');

  return (
    <div
      id="evap_pack_new"
      ref={rootRef}
      className="input-part row evap_pack evap_pack-rating-mode"
    >
      {/* ===== INPUT: sidebar rail + tab content (tmpl:3-24) =====
          The rail is a col-xs-1 SIDEBAR, not a bare <ul>: every
          `#rating_mode_evap_pack_content .col-xs-1.nav.nav-tabs …` rule in
          common.css (:25018-25050) is a descendant selector on this shape. */}
      <div className="col-xs-1 nav nav-tabs">
        <div className="">
          <ul className="col-xs-12">
            <li className="cursor-default">Input</li>
            {INPUT_TABS.map(([name, label]) => (
              <li
                key={name}
                className={`evap_sel_${name} input-nav-item${
                  activeTab === name ? ' selected' : ''
                }`}
                data-name={name}
                data-tab={name}
                onClick={() => setActiveTab(name)}
              >
                <a>{label}</a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="col-xs-11 content-data tab-content">
        {/* ===== TAB 1: MEDIA ===== */}
        <div
          className={`tab-pane input-tab-pane scrollable-content float-left${
            activeTab === 'media' ? ' in active' : ''
          }`}
          id="evap_sel_media"
        >
          <div className="col-xs-5 col-md-5 col-lg-5">
            <SmartlistStrip
              type="media"
              counts={smartlist.media}
              style={{ marginRight: '-11px' }}
              onToggle={toggleSmartlist}
              onReset={resetSmartlist}
            />
            <Row label="Manufacturer">
              <select
                name="mfg_id"
                className="media_params_field"
                value={sel(mediaParams.mfg_id)}
                onChange={(e) => onParamsField('media_params', 'mfg_id', e.target.value)}
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'media.manufacturer'), mediaParams.mfg_id, 'name')}
              </select>
            </Row>
            <Row label="Series">
              <select
                name="model_id"
                className="media_params_field"
                value={sel(mediaParams.model_id)}
                onChange={(e) => onParamsField('media_params', 'model_id', e.target.value)}
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'media.model'), mediaParams.model_id, 'name')}
              </select>
            </Row>
            <Row label="Edge Finish">
              <select
                name="edge_finish_id"
                className="media_params_field"
                value={sel(mediaParams.edge_finish_id)}
                onChange={(e) =>
                  onParamsField('media_params', 'edge_finish_id', e.target.value)
                }
              >
                <option value="None">- -</option>
                {opt(cmsList(cms, 'media.edge_finish'), mediaParams.edge_finish_id, 'name')}
              </select>
            </Row>
            <Row label="Depth" unit="in">
              <select
                name="depth_id"
                className="media_params_field"
                value={sel(mediaParams.depth_id)}
                onChange={(e) => onParamsField('media_params', 'depth_id', e.target.value)}
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'media.depth'), mediaParams.depth_id, 'value')}
              </select>
            </Row>
            {/* tmpl:115-121 — Drift Eliminator is a CHECKBOX + select pair, not
                a bare select. The checkbox is the on/off; unticking it clears
                the depth and greys the select via `non-clickable`
                (changeModelField :521-529). `common.css:25838` styles the row
                off `.input-part .col-xs-12:has(> input[type=checkbox]
                .model_field)`, so both controls must sit in the SAME
                `.col-xs-12` — which is what `Row` renders. */}
            <Row label="Drift Eliminator" unit="in" name="delim_depth_id">
              <input
                type="checkbox"
                name="delim_depth_id"
                className="model_field"
                checked={delimOn}
                onChange={(e) => {
                  setDelimOn(e.target.checked);
                  if (e.target.checked) {
                    // Legacy only un-greys the select and leaves the depth
                    // unset (:521-529); `_autoSelectDelimDepth` (:290-296)
                    // separately fills it when the CMS offers exactly one.
                    const depths = cmsList(cms, 'drift_eliminator.depth');
                    if (depths.length === 1) {
                      onModelField('delim_depth_id', String(depths[0].id));
                    }
                  } else {
                    onModelField('delim_depth_id', '');
                  }
                }}
              />
              <select
                name="delim_depth_id"
                className={`model_field${delimOn ? '' : ' non-clickable'}`}
                value={sel(model.delim_depth_id)}
                onChange={(e) => onModelField('delim_depth_id', e.target.value)}
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'drift_eliminator.depth'), model.delim_depth_id, 'value')}
              </select>
            </Row>
            <Row label="Blank-Off" name="blank_off_mtl_gauge_id">
              <select
                name="blank_off_mtl_gauge_id"
                className="model_field"
                value={sel(model.blank_off_mtl_gauge_id)}
                onChange={(e) => onModelField('blank_off_mtl_gauge_id', e.target.value)}
              >
                <option value="None">- -</option>
                {blankOffList.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {catalogLabel(b)}
                  </option>
                ))}
              </select>
            </Row>
          </div>

          {/* Right column (tmpl:151-288). The evap-mount-wrapper / flip-card /
              flip-inner / front+back nest is what `#evap_pack_new
              .evap-mount-wrapper …` (common.css:25856-25925) styles: the
              front's `padding: 0 5px 10px`, the back's `rotateX(180deg)`, and
              the `[data-tab=smartlist]` rule that rotates the inner to reveal
              it. data-tab is the ONLY thing that flips the card. */}
          <div className="col-xs-7 evap-flip-col" style={{ marginTop: 12 }}>
            <div
              className="evap-mount-wrapper"
              id="evap-media-flip"
              data-tab={slOpen.current.media ? 'smartlist' : 'front'}
            >
              <div className="mounting-flip-card">
                <div className="mounting-flip-inner">
                  <div className="mounting-flip-front" data-season={seasonDisplayValue}>
                    <div className="row" style={{ marginTop: 27 }}>
                      <div className="col-xs-5 label-part">
                        <span data-name="air_stream_id">Air Stream</span>
                      </div>
                      <div className="col-xs-7 input-part row">
                        <select
                          name="air_stream_id"
                          className="model_field"
                          style={{ marginLeft: 15 }}
                          value={sel(model.air_stream_id)}
                          onChange={(e) => onModelField('air_stream_id', e.target.value)}
                        >
                          <option value="None">- -</option>
                          {opt(airStream, model.air_stream_id, 'name')}
                        </select>
                      </div>
                    </div>

                    {/* Airflow renders through legacy's render-time
                        `validateNumberField` pass (js:466-470): no
                        `formatter_type` on this input (tmpl:180-181), so it
                        takes the integer branch —
                        `formatNumberWithCommaSeparator(Math.round(v))`, i.e.
                        `10,000`. React printed the raw model value. The commit
                        stays per-keystroke as it always has, but now goes
                        through `commitNumberValue` so the separators are
                        stripped before the value reaches staging.

                        `unformatOnFocus` drops the separators back off while
                        the box is being edited (`10,000` at rest, `10000`
                        under the caret) — `NumericInputFormatter`'s focus/blur
                        pair, formatter.js:207-225. */}
                    <Row label="Airflow" unit="CFM" name="airflow_actual">
                      <SelectorNumberInput
                        name="airflow_actual"
                        formatter="integer"
                        min={0}
                        max={4000000}
                        className="model_field w-100"
                        unformatOnFocus
                        value={model.airflow_actual}
                        onChange={(e) => {
                          const v = commitNumberValue(e.currentTarget);
                          onModelField('airflow_actual', v == null ? '' : String(v));
                        }}
                      />
                    </Row>

                    {/* Dev-sync J6 — Season select (sec_field) + the
                        season_selected_id-gated per-season Entering rows. */}
                    <div className="row">
                      <div className="col-xs-5 label-part">
                        <span data-name="season_id">Season</span>
                      </div>
                      <div className="col-xs-7 input-part row">
                        <select
                          name="season_id"
                          className="sec_field"
                          style={{ marginLeft: 15 }}
                          value={seasonSelectedId == null ? '' : String(seasonSelectedId)}
                          onChange={(e) => onSeasonField(e.target.value)}
                        >
                          {evapSeasons.map((r) => (
                            <option key={String(r.id)} value={String(r.id)}>
                              {String(r.name ?? '')}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {(
                      [
                        ['summer', showSummer, 'Summer'],
                        ['winter', showWinter, 'Winter'],
                      ] as Array<['summer' | 'winter', boolean, string]>
                    ).map(([season, show, heading]) =>
                      show ? (
                        <Fragment key={season}>
                          <div className={`row season-group-${season}`}>
                            <div className="col-xs-5 label-part">
                              <span style={{ fontWeight: 700 }}>{heading}</span>
                            </div>
                          </div>
                          {(
                            [
                              ['eadb', 'Entering Air Dry Bulb'],
                              ['eawb', 'Entering Air Wet Bulb'],
                            ] as Array<['eadb' | 'eawb', string]>
                          ).map(([half, label]) => (
                            <Row
                              key={`${half}_${season}`}
                              label={label}
                              unit="°F"
                              name={`${half}_temp_${season}`}
                              rowClass={`season-group-${season}`}
                            >
                              {/* `data-formatter_type="decimal"` (tmpl:212-213):
                                  legacy reformats these to `toFixed(2)` on
                                  every render (js:458-462 and again through
                                  `validateNumberField` :466-470), so an
                                  entering temp of 87 reads `87.00`. React
                                  echoed the raw number back on blur
                                  (`e.currentTarget.value = String(v)`) and
                                  seeded the box with `defaultValue`, which a
                                  later re-seed could not refresh; both go away
                                  with the shared formatted input, whose focus
                                  draft is what keeps typing intact. Commit is
                                  still blur-only — legacy binds
                                  `change .sec_field` (js:148). */}
                              <SelectorNumberInput
                                name={`${half}_temp_${season}`}
                                formatter="decimal"
                                min={8}
                                max={120}
                                className="sec_field w-100"
                                commitOn="blur"
                                value={sectionDraft.current[`${half}_temp_${season}`]}
                                onChange={(e) => {
                                  onSectionField(`${half}_temp_${season}`, e.target.value);
                                }}
                              />
                            </Row>
                          ))}
                        </Fragment>
                      ) : null
                    )}
                  </div>

                  <SmartlistPanel
                    type="media"
                    items={smartlist.media.data}
                    selectedId={model.master_media_id}
                    onRow={(id) => applySmartlistItem('media', id, smartlist.media.data)}
                    onLoadMore={() => loadMoreSmartlist('media')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== TAB 2: CONSTRUCTION ===== */}
        <div
          className={`tab-pane input-tab-pane scrollable-content float-left${
            activeTab === 'construction' ? ' in active' : ''
          }`}
          id="evap_sel_construction"
          style={{ marginTop: 12 }}
        >
          <div className="col-xs-5 col-md-5 col-lg-5">
            <Row label="Sump Material" name="sump_mtl_gauge_id">
              <select
                name="sump_mtl_gauge_id"
                className="model_field"
                value={sel(model.sump_mtl_gauge_id)}
                onChange={(e) => onModelField('sump_mtl_gauge_id', e.target.value)}
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'sump_mtl_gauge'), model.sump_mtl_gauge_id, 'name')}
              </select>
            </Row>
            {/* NO "Wet Metals" row: legacy's Construction tab is exactly three
                rows (tmpl:293-347 — Sump Material, Piping Material, Spray Pipe
                Material). `drip_tray_mtl_gauge_id` is not editable from this
                dialog; it appears in the OUTPUT summary only, as "Wet Parts
                Material" (`outWetParts` above / output.tmpl:100). */}
            <Row label="Piping Material">
              <select
                name="pipe_mtl_sch_id"
                className="model_field"
                value={sel(model.pipe_mtl_sch_id)}
                onChange={(e) => onModelField('pipe_mtl_sch_id', e.target.value)}
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'piping'), model.pipe_mtl_sch_id, 'name')}
              </select>
            </Row>
            <Row label="Spray Pipe Material" name="spray_pipe_splash_id">
              <select
                name="spray_pipe_splash_id"
                className="model_field"
                value={sel(model.spray_pipe_splash_id)}
                onChange={(e) => onModelField('spray_pipe_splash_id', e.target.value)}
              >
                <option value="None">- -</option>
                {opt(cmsList(cms, 'splash_pipe'), model.spray_pipe_splash_id, 'name')}
              </select>
            </Row>
          </div>
        </div>

        {/* ===== TAB 3: PUMP & CONNECTIONS ===== */}
        <div
          className={`tab-pane input-tab-pane scrollable-content float-left${
            activeTab === 'pump' ? ' in active' : ''
          }`}
          id="evap_sel_pump"
        >
          <div className="col-xs-5 col-md-5 col-lg-5">
            <SmartlistStrip
              type="pump"
              counts={smartlist.pump}
              onToggle={toggleSmartlist}
              onReset={resetSmartlist}
            />
            <div className="row section-subheading" style={{ paddingTop: 7 }}>
              <strong>Pump</strong>
            </div>
            <Row label="Type" name="type_id">
              <select
                name="type_id"
                className="pump_params_field"
                value={sel(pumpParams.type_id)}
                onChange={(e) => onParamsField('pump_params', 'type_id', e.target.value)}
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'pump.type'), pumpParams.type_id, 'name')}
              </select>
            </Row>
            <Row label="Manufacturer" name="mfg_id">
              <select
                name="mfg_id"
                className="pump_params_field"
                value={sel(pumpParams.mfg_id)}
                onChange={(e) => onParamsField('pump_params', 'mfg_id', e.target.value)}
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'pump.manufacturer'), pumpParams.mfg_id, 'name')}
              </select>
            </Row>
            <Row label="Model" name="model">
              <select
                name="model"
                className="pump_params_field"
                value={sel(pumpParams.model)}
                onChange={(e) => onParamsField('pump_params', 'model', e.target.value)}
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'pump.model'), pumpParams.model, 'model', 'model')}
              </select>
            </Row>
            <Row label="Power" unit="HP" name="power">
              <ReadOnly value={pumpParams.power} />
            </Row>
            <Row label="Voltage" name="voltage">
              <ReadOnly value={pumpParams.voltage} decimal={false} />
            </Row>

            {/* tmpl:448 — Piping repeats the two construction-tab materials
                under their own subheading. `.section-subheading.piping` carries
                its own padding-top (common.css:24881). */}
            <div className="row section-subheading piping">
              <strong>Piping</strong>
            </div>
            <Row label="Piping Material" name="pipe_mtl_sch_id">
              <select
                name="pipe_mtl_sch_id"
                className="model_field"
                value={sel(model.pipe_mtl_sch_id)}
                onChange={(e) => onModelField('pipe_mtl_sch_id', e.target.value)}
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'piping'), model.pipe_mtl_sch_id, 'name')}
              </select>
            </Row>
            <Row label="Splash Pipe Material" name="spray_pipe_splash_id">
              <select
                name="spray_pipe_splash_id"
                className="model_field"
                value={sel(model.spray_pipe_splash_id)}
                onChange={(e) => onModelField('spray_pipe_splash_id', e.target.value)}
              >
                <option value="None">- -</option>
                {opt(cmsList(cms, 'splash_pipe'), model.spray_pipe_splash_id, 'name')}
              </select>
            </Row>
          </div>

          <div
            className="col-xs-7 evap-flip-col"
            style={{ marginTop: 13, paddingRight: 0 }}
          >
            <div
              className="evap-mount-wrapper"
              id="evap-pump-flip"
              data-tab={slOpen.current.pump ? 'smartlist' : 'front'}
            >
            <div className="mounting-flip-card">
            <div className="mounting-flip-inner">
            <div className="mounting-flip-front">
            {/* ----- Fill ----- */}
            <div className="row section-subheading" style={{ paddingTop: 33 }}>
              <strong>Fill</strong>
            </div>
            <Row label="Connection">
              <select
                name="nipple_id"
                className="fill_params_field model_field"
                value={sel(fillParams.nipple_id)}
                onChange={(e) => onParamsField('fill_params', 'nipple_id', e.target.value)}
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'fill'), fillParams.nipple_id, 'nipple', 'nipple_id')}
              </select>
            </Row>
            <Row label="Hand">
              <div className="checkboxes-group">
                {cmsList(cms, 'fill_params.connection_hand').map((r) => (
                  <label className="radio" key={String(r.id)}>
                    <input
                      type="radio"
                      className="model_field"
                      name="fill_conn_hand_id"
                      value={String(r.id)}
                      checked={eq(r.id, model.fill_conn_hand_id)}
                      onChange={() => onModelField('fill_conn_hand_id', String(r.id))}
                    />
                    {String(r.name ?? '')}
                  </label>
                ))}
              </div>
            </Row>
            <Row label="Valve">
              <select
                name="fill_automatic_valve_id"
                className="model_field"
                value={sel(model.fill_automatic_valve_id)}
                onChange={(e) => onModelField('fill_automatic_valve_id', e.target.value)}
              >
                <option value="None">- -</option>
                {opt(valveLists.fill, model.fill_automatic_valve_id, 'name')}
              </select>
            </Row>

            {/* ----- Drain ----- */}
            <div className="row section-subheading">
              <strong>Drain</strong>
            </div>
            <Row label="Connection">
              <select
                name="nipple_id"
                className="drain_params_field model_field"
                value={sel(drainParams.nipple_id)}
                onChange={(e) => onParamsField('drain_params', 'nipple_id', e.target.value)}
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'drain'), drainParams.nipple_id, 'nipple', 'nipple_id')}
              </select>
            </Row>
            <Row label="Hand">
              <div className="checkboxes-group">
                {cmsList(cms, 'drain_params.connection_hand').map((r) => (
                  <label className="radio" key={String(r.id)}>
                    <input
                      type="radio"
                      className="model_field"
                      name="drain_conn_hand_id"
                      value={String(r.id)}
                      checked={eq(r.id, model.drain_conn_hand_id)}
                      onChange={() => onModelField('drain_conn_hand_id', String(r.id))}
                    />
                    {String(r.name ?? '')}
                  </label>
                ))}
              </div>
            </Row>
            <Row label="Valve">
              <select
                name="drain_automatic_valve_id"
                className="drain_params_field model_field"
                value={sel(model.drain_automatic_valve_id)}
                onChange={(e) => onModelField('drain_automatic_valve_id', e.target.value)}
              >
                <option value="None">- -</option>
                {opt(valveLists.drain, model.drain_automatic_valve_id, 'name')}
              </select>
            </Row>

            {/* ----- Overflow ----- */}
            <div className="row section-subheading">
              <strong>Overflow</strong>
            </div>
            <Row label="Connection">
              <select
                name="nipple_id"
                className="overflow_params_field model_field"
                value={sel(overflowParams.nipple_id)}
                onChange={(e) =>
                  onParamsField('overflow_params', 'nipple_id', e.target.value)
                }
              >
                <option value="None" className="hide">- -</option>
                {opt(cmsList(cms, 'overflow'), overflowParams.nipple_id, 'nipple', 'nipple_id')}
              </select>
            </Row>
            {/* NO Overflow "Valve" row, and no "Hand" row: legacy's Overflow
                group is Connection ALONE (tmpl:563-574), unlike Fill and Drain.
                `_buildValveLists` does still build the overflow list
                (selector.js:323-328) but nothing renders it — dead in legacy,
                kept above so the three lists stay a faithful port. The output
                summary agrees: its Overflow row is the nipple only
                (output.tmpl:164-169 / `outOverflow`). */}
            </div>

            <SmartlistPanel
              type="pump"
              items={smartlist.pump.data}
              selectedId={model.master_pump_id}
              onRow={(id) => applySmartlistItem('pump', id, smartlist.pump.data)}
              onLoadMore={() => loadMoreSmartlist('pump')}
            />
            </div>
            </div>
            </div>
          </div>
        </div>

        {/* ===== TAB 4: OPTIONS (tmpl:605-647) ===== */}
        <div
          className={`tab-pane input-tab-pane scrollable-content float-left${
            activeTab === 'options' ? ' in active' : ''
          }`}
          id="evap_sel_options"
          style={{ marginTop: 12 }}
        >
          <div className="col-xs-5 col-md-5 col-lg-5">
            <Row label="Time Clock" name="time_clock_id">
              <select
                name="time_clock_id"
                className="model_field"
                value={sel(model.time_clock_id)}
                onChange={(e) => onModelField('time_clock_id', e.target.value)}
              >
                <option value="None">- -</option>
                {opt(cmsList(cms, 'timeclock'), model.time_clock_id, 'name')}
              </select>
            </Row>
            <Row label="Freez-Stat" name="db_temp_sensor_spn_id">
              <select
                name="db_temp_sensor_spn_id"
                className="model_field"
                value={sel(model.db_temp_sensor_spn_id)}
                onChange={(e) => onModelField('db_temp_sensor_spn_id', e.target.value)}
              >
                <option value="None">- -</option>
                {opt(cmsList(cms, 'freez_stat'), model.db_temp_sensor_spn_id, 'name')}
              </select>
            </Row>
          </div>
        </div>
      </div>

      {/* ===== OUTPUT SECTION (evap_pack.selector.output.tmpl) =====
          A SIBLING of the input rail + content-data, not a child: legacy
          render_output() paints the output template into
          `.evap_pack-output-section`, and it carries its own col-xs-1 rail. */}
      <div className="output-part evap_pack-output-section">
        <div className="col-xs-1 nav nav-tabs">
          <div className="">
            <ul className="col-xs-12 output-menu-items">
              <li className="cursor-default">Output</li>
              <li className="output-nav-item selected" data-name="summary">
                <a>Summary</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="col-xs-11 with-border tab-content evap_pack-rd-output">
          <div
            className="output-tab-pane tab-pane fade active in scrollable-content float-left"
            id="evap_pack_summary_output"
          >
            <div className="col-xs-5 col-md-5 col-lg-5">
              <OutRow label="Media">
                <span>{outMedia}</span>
              </OutRow>
              {/* "Drif Eliminator" — legacy's typo, kept. */}
              <OutRow label="Drif Eliminator" unit="in">
                <span>{outDelim}</span>
              </OutRow>
              <OutRow label="Sump Length" unit="in">
                <ReadOnly value={model.sump_length} />
              </OutRow>
              <OutRow label="Spray Pipe Dia" unit="in" hidden>
                <ReadOnly value={model.spray_pipe_dia} />
              </OutRow>
              <OutRow label="Spray Splash Pipe Dia" unit="in" hidden>
                <ReadOnly value={model.spray_pipe_splash_dia} />
              </OutRow>
              <OutRow label="Wet Parts Material">
                <span>{outWetParts}</span>
              </OutRow>
              <OutRow label="Pump">
                <span className="output-text-clip">{outPump}</span>
              </OutRow>
              <OutRow label="Piping Material">
                <span className="output-text-clip">{outPiping}</span>
              </OutRow>
              <OutRow label="Fill">
                <span className="output-text-clip">{outFill}</span>
              </OutRow>
              <OutRow label="Drain">
                <span className="output-text-clip">{outDrain}</span>
              </OutRow>
              <OutRow label="Overflow">
                <span>{outOverflow}</span>
              </OutRow>
            </div>

            <div className="col-xs-7 col-md-7 col-lg-7" style={{ paddingLeft: 20 }}>
              <OutRow label="Air Stream">
                <span>{outAirStream}</span>
              </OutRow>
              <OutRow label="Airflow*" unit="CFM">
                <ReadOnly value={model.airflow_actual} integer />
              </OutRow>
              {/* Dev-sync J6 — per-season leaving-air rows
                  (evap_pack.selector.output.tmpl:210-283). ★ Deviation
                  (survey §B6/§J6 "do not port"): legacy gates these on RAW
                  section.season_id while its js only defaults season_value →
                  a fresh section showed NO air conditions at all (a real
                  bug). Gated here on season_selected_id — the same defaulted
                  season the input side uses, so a fresh section shows the
                  Summer rows. */}
              {showSummer && (
                <>
                  <OutRow label="Summer Leaving Air DB" unit="°F">
                    <ReadOnly text={oneDec(leaving.ladb_temp_summer_effective)} />
                  </OutRow>
                  <OutRow label="Summer Leaving Air WB" unit="°F">
                    <ReadOnly text={oneDec(leaving.lawb_temp_summer_effective)} />
                  </OutRow>
                </>
              )}
              {showWinter && (
                <>
                  <OutRow label="Winter Leaving Air DB" unit="°F">
                    <ReadOnly text={oneDec(leaving.ladb_temp_winter_effective)} />
                  </OutRow>
                  <OutRow label="Winter Leaving Air WB" unit="°F">
                    <ReadOnly text={oneDec(leaving.lawb_temp_winter_effective)} />
                  </OutRow>
                </>
              )}
              <OutRow label="Airway Width x Height" unit="in">
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {oneDec(model.media_aw_width)} x {oneDec(model.media_aw_height)}
                </span>
              </OutRow>
              <OutRow label="Face Velocity" unit="fpm">
                <ReadOnly value={model.face_velocity_actual} integer />
              </OutRow>
              <OutRow label="APD" unit="in WC">
                <ReadOnly value={model.media_apd} />
              </OutRow>
              <OutRow label="Cooling Effectiveness" unit="%">
                <ReadOnly text={String(model.media_clg_eff_percent ?? '')} />
              </OutRow>
              <OutRow label="Water Flow" unit="GPM">
                <ReadOnly value={model.water_flow} />
              </OutRow>
              {/* Sync K (tmpl:369-384): Make-Up Water joins after Water Flow. */}
              <OutRow label="Make-Up Water" unit="GPH">
                <ReadOnly value={model.make_up_water_rate} />
              </OutRow>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// small presentational helpers (legacy .row label/input scaffolding)
// ---------------------------------------------------------------------------
/**
 * The `x / y · Reset · Smartlist` strip (tmpl:29-38 media, :354-362 pump).
 * `warning` when the filtered count is falsy — legacy's `{{#unless count}}`,
 * i.e. a filter that matches nothing turns the badge red. A null count (still
 * loading) renders four non-breaking spaces so the button does not resize.
 */
const SmartlistStrip = ({
  type,
  counts,
  style,
  onToggle,
  onReset,
}: {
  type: EvapSmartlistType;
  counts: { count: number | null; cmsCount: number | null };
  style?: React.CSSProperties;
  onToggle: (t: EvapSmartlistType) => void;
  onReset: (t: EvapSmartlistType) => void;
}) => (
  <div className="row smartlist-count-row" style={style}>
    <div className="col-xs-12">
      <button
        id={`${type}-btn`}
        data-type={type}
        type="button"
        className={`btn btn-sync vis-damper${
          counts.count ? '' : ' warning'
        } clickable smartlist-toggle-btn`}
        onClick={() => onToggle(type)}
      >
        <span className="search-result">
          {counts.count != null ? counts.count : '    '} /{' '}
          {counts.cmsCount ?? ''}
        </span>
        <i className="icon-list btn-icon-sync">Smartlist</i>
      </button>
      <span
        className="icon-refresh-1 reset-btn smartlist-reset"
        data-type={type}
        style={{ marginLeft: 8 }}
        onClick={() => onReset(type)}
      >
        Reset
      </span>
    </div>
  </div>
);

/**
 * The flip-BACK smartlist panel (tmpl:267-288 media, :578-598 pump) and the
 * rows legacy builds in `appendSmartlistItems` (:895-929). Media shows one
 * column (the significant part number); pump shows manufacturer / type / model
 * / power. Infinite scroll fires within 100px of the bottom, as legacy's
 * `onscroll` (:448-453).
 */
const SmartlistPanel = ({
  type,
  items,
  selectedId,
  onRow,
  onLoadMore,
}: {
  type: EvapSmartlistType;
  items: ISmartlistItem[];
  selectedId: unknown;
  onRow: (id: number) => void;
  onLoadMore: () => void;
}) => (
  <div className="mounting-flip-back">
    <div className="mounting-smartlist-content">
      <div className="">
        <div className="filter-list">
          <div className="col-xs-12 padding-right-0">
            <ul className="list-heading list-inline">
              <li className={`only-for-evap-${type} custom-columns`}>
                {type === 'media' ? (
                  <h4>SIGNIFICANT PART NUMBER</h4>
                ) : (
                  <>
                    <h4 className="w-200">MANUFACTURER</h4>
                    <h4 className="w-100">TYPE</h4>
                    <h4 className="w-100">MODEL</h4>
                    <h4 className="w-60 text-align-right">POWER (HP)</h4>
                  </>
                )}
              </li>
            </ul>
            <ul
              className="list-rows scrollable catalogue-rows-container smartlist-items evap-smartlist-items"
              data-type={type}
              tabIndex={0}
              onScroll={(e) => {
                const el = e.currentTarget;
                if (el.scrollHeight - (el.scrollTop + el.clientHeight) < 100) onLoadMore();
              }}
            >
              {items.length === 0 && <li className="smartlist-no-results">No results</li>}
              {items.map((item) => (
                <li
                  key={item.id}
                  className={`smartlist-row${eq(item.id, selectedId) ? ' synced' : ''}`}
                  data-id={item.id}
                  data-type={type}
                  onClick={() => onRow(item.id)}
                >
                  <ul className="table-item">
                    <li className="xxlong text-overflow-unset">
                      {type === 'media' ? (
                        <span>{String(item.spn ?? '')}</span>
                      ) : (
                        <span className="custom-columns-row true">
                          <span className="w-150">{String(item.mfg_name ?? '')}</span>
                          <span className="w-150">{String(item.type_name ?? '')}</span>
                          <span className="w-100">{String(item.model ?? '')}</span>
                          <span className="w-60 text-align-right">
                            {item.power != null ? String(item.power) : ''}
                          </span>
                        </span>
                      )}
                    </li>
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/**
 * A legacy input row. The `.col-xs-7.input-part > .row > .col-xs-12` nest is
 * load-bearing, not decoration — `#evap_pack_new .input-part .col-xs-12:has(…)`
 * (common.css:25838) and the `.col-xs-7.input-part .col-xs-12` width rules
 * (:25476) are descendant selectors on exactly this shape, so a control dropped
 * straight into `.input-part` picks up none of them.
 */
const Row = ({
  label,
  unit,
  name,
  rowClass,
  children,
}: {
  label: string;
  unit?: string;
  name?: string;
  rowClass?: string;
  children: React.ReactNode;
}) => (
  <div className={rowClass ? `row ${rowClass}` : 'row'}>
    <div className="col-xs-5 label-part">
      <span data-name={name}>{label}</span>
      {unit && <span className="unit right">{unit}</span>}
    </div>
    <div className="col-xs-7 input-part">
      <div className="row">
        <div className="col-xs-12">{children}</div>
      </div>
    </div>
  </div>
);

/** Read-only display input. `text` short-circuits the decimal formatter (the
 *  get_one_decimal rows already carry a formatted string).
 *
 *  `render_output` runs `validateNumberField` over `input[data-formatter_type]`
 *  (js:490-497), so the INTEGER outputs — Airflow* (output.tmpl:202) and Face
 *  Velocity (:308) — carry thousands separators too. React printed those two
 *  raw; the decimal ones were already formatted. */
const ReadOnly = ({
  value,
  text,
  decimal = true,
  integer = false,
}: {
  value?: unknown;
  text?: string;
  decimal?: boolean;
  integer?: boolean;
}) => (
  <input
    type="text"
    data-formatter_type={integer ? 'integer' : decimal ? 'decimal' : undefined}
    value={
      text !== undefined
        ? text
        : integer
          ? formatIntegerDisplay(value)
          : !decimal
            ? String(value ?? '')
            : formatDecimalDisplay(value)
    }
    readOnly
    tabIndex={-1}
    onChange={() => undefined}
  />
);

/**
 * A legacy OUTPUT row (evap_pack.selector.output.tmpl). The
 * `.col-xs-12.output-data > .output-without-unit` nest is what
 * `#rating_mode_evap_pack_content .output-part .output-without-unit[ span]`
 * (common.css:25701-25710) styles — the grey 13px type and the ellipsis clip
 * both come from there, so the wrapper cannot be flattened away.
 */
const OutRow = ({
  label,
  unit,
  hidden,
  children,
}: {
  label: string;
  unit?: string;
  hidden?: boolean;
  children: React.ReactNode;
}) => (
  <div className={hidden ? 'row hide' : 'row'}>
    <div className="col-xs-5 label-part">
      {label}
      {unit && <span className="unit right">{unit}</span>}
    </div>
    <div className="col-xs-7 input-part">
      <div className="row">
        <div className="col-xs-12 output-data">
          <div className="output-without-unit">{children}</div>
        </div>
      </div>
    </div>
  </div>
);

export default EvapPackSelector;
